import { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Users, UserCheck, Mail, Trash2, Download, Database, Target, FlaskConical } from 'lucide-react';

// Import tab components
import UsersTab from '../components/admin/UsersTab';
import RequestsTab from '../components/admin/RequestsTab';
import EmailTab from '../components/admin/EmailTab';
import BackupTab from '../components/admin/BackupTab';
import RestoreTab from '../components/admin/RestoreTab';
import DbStatsTab from '../components/admin/DbStatsTab';
import TargetsAdminTab from '../components/admin/TargetsAdminTab';
import TestSeedTab from '../components/admin/TestSeedTab';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '')}/api`;

export default function Admin() {
  const [activeTab, setActiveTab] = useState('users');
  const [isTestEnv, setIsTestEnv] = useState(false);

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isApprover = currentUser.role === 'approver' || currentUser.role === 'super_approver';
  const isSuperApprover = currentUser.role === 'super_approver';

  useEffect(() => {
    if (!isSuperApprover) return;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/admin/env-info`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setIsTestEnv(Boolean(r.data?.is_test_env));
      } catch {
        setIsTestEnv(false);
      }
    })();
  }, [isSuperApprover]);

  const superColsClass = isTestEnv ? 'grid-cols-8' : 'grid-cols-7';

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-amber-600 text-sm font-medium mb-1">Administration</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Admin Panel
            </h1>
            <p className="text-slate-500 text-sm mt-1">Manage users, requests, and system settings</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={`grid w-full ${isSuperApprover ? superColsClass : 'grid-cols-2'} h-12 p-1 bg-slate-100 rounded-xl`}>
            <TabsTrigger 
              value="users" 
              className="flex items-center gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
              data-testid="tab-users"
            >
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Users</span>
            </TabsTrigger>
            {isApprover && (
              <TabsTrigger 
                value="requests" 
                className="flex items-center gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="tab-requests"
              >
                <UserCheck className="w-4 h-4" />
                <span className="hidden sm:inline">Requests</span>
              </TabsTrigger>
            )}
            {isSuperApprover && (
              <>
                <TabsTrigger 
                  value="email" 
                  className="flex items-center gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
                  data-testid="tab-email"
                >
                  <Mail className="w-4 h-4" />
                  <span className="hidden sm:inline">Email</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="restore" 
                  className="flex items-center gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
                  data-testid="tab-restore"
                >
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">Restore</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="backup" 
                  className="flex items-center gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
                  data-testid="tab-backup"
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="hidden sm:inline">Delete</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="db-stats" 
                  className="flex items-center gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
                  data-testid="tab-db-stats"
                >
                  <Database className="w-4 h-4" />
                  <span className="hidden sm:inline">DB Stats</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="targets" 
                  className="flex items-center gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
                  data-testid="tab-admin-targets"
                >
                  <Target className="w-4 h-4" />
                  <span className="hidden sm:inline">Targets</span>
                </TabsTrigger>
                {isTestEnv && (
                  <TabsTrigger
                    value="test-seed"
                    className="flex items-center gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
                    data-testid="tab-test-seed"
                  >
                    <FlaskConical className="w-4 h-4" />
                    <span className="hidden sm:inline">Test Seed</span>
                  </TabsTrigger>
                )}
              </>
            )}
          </TabsList>

          {/* Tab Contents */}
          <TabsContent value="users" className="mt-6">
            <UsersTab />
          </TabsContent>

          {isApprover && (
            <TabsContent value="requests" className="mt-6">
              <RequestsTab />
            </TabsContent>
          )}

          {isSuperApprover && (
            <>
              <TabsContent value="email" className="mt-6">
                <EmailTab />
              </TabsContent>

              <TabsContent value="restore" className="mt-6">
                <RestoreTab />
              </TabsContent>

              <TabsContent value="backup" className="mt-6">
                <BackupTab />
              </TabsContent>

              <TabsContent value="db-stats" className="mt-6">
                <DbStatsTab />
              </TabsContent>

              <TabsContent value="targets" className="mt-6">
                <TargetsAdminTab />
              </TabsContent>

              {isTestEnv && (
                <TabsContent value="test-seed" className="mt-6">
                  <TestSeedTab />
                </TabsContent>
              )}
            </>
          )}
        </Tabs>
      </div>
    </Layout>
  );
}
